<template>
  <div class="cal">
    <div class="cal-toolbar">
      <div class="d-flex align-items-center" style="gap: 6px;">
        <button type="button" class="btn btn-sm btn-light" aria-label="Previous" @click="move(-1)"><i class="fe fe-chevron-left"></i></button>
        <button type="button" class="btn btn-sm btn-light" @click="goToday">Today</button>
        <button type="button" class="btn btn-sm btn-light" aria-label="Next" @click="move(1)"><i class="fe fe-chevron-right"></i></button>
        <h6 class="mb-0 ml-2 font-weight-bold">{{ heading }}</h6>
      </div>
      <div class="d-flex align-items-center flex-wrap" style="gap: 10px;">
        <span class="cal-legend"><i class="cal-dot cal-dot--physical"></i>Physical</span>
        <span class="cal-legend"><i class="cal-dot cal-dot--virtual"></i>Virtual</span>
        <span class="cal-legend"><i class="cal-dot cal-dot--telemedicine"></i>Telemedicine</span>
        <div class="btn-group btn-group-sm" role="group" aria-label="Calendar view">
          <button type="button" class="btn" :class="mode === 'month' ? 'btn-primary' : 'btn-light'" @click="mode = 'month'">Month</button>
          <button type="button" class="btn" :class="mode === 'week' ? 'btn-primary' : 'btn-light'" @click="mode = 'week'">Week</button>
        </div>
      </div>
    </div>

    <div v-if="loading" class="small text-muted px-2 pb-2">Loading…</div>

    <!-- Month -->
    <div v-if="mode === 'month'" class="cal-month">
      <div v-for="d in weekdayNames" :key="d" class="cal-wd">{{ d }}</div>
      <div v-for="day in monthDays" :key="day.date" class="cal-cell"
        :class="{ 'cal-cell--out': !day.inMonth, 'cal-cell--today': day.date === today }">
        <div class="cal-num">{{ day.num }}</div>
        <router-link v-for="a in (byDate[day.date] || []).slice(0, 4)" :key="a.id" :to="'/appointment/' + a.id"
          class="cal-ev" :class="['cal-ev--' + a.type, 'cal-ev--st-' + a.status]" :title="tooltip(a)">
          {{ a.time }} {{ a.patient || 'Patient' }}
        </router-link>
        <button v-if="(byDate[day.date] || []).length > 4" type="button" class="cal-more" @click="openWeek(day.date)">
          +{{ byDate[day.date].length - 4 }} more
        </button>
      </div>
    </div>

    <!-- Week -->
    <div v-else class="cal-week">
      <div v-for="day in weekDays" :key="day.date" class="cal-wcol" :class="{ 'cal-cell--today': day.date === today }">
        <div class="cal-whead">{{ day.label }}</div>
        <router-link v-for="a in (byDate[day.date] || [])" :key="a.id" :to="'/appointment/' + a.id"
          class="cal-wev" :class="['cal-ev--' + a.type, 'cal-ev--st-' + a.status]">
          <strong>{{ a.time }}</strong> · {{ a.minutes }} min
          <div>{{ a.patient || 'Patient' }}</div>
          <div class="cal-wsub">
            {{ a.type }}<span v-if="a.doctor"> · {{ a.doctor }}</span><span v-if="a.direction === 'sent'"> · sent to {{ a.facility }}</span>
          </div>
          <div class="cal-wsub text-capitalize">{{ a.status }}</div>
        </router-link>
        <div v-if="!(byDate[day.date] || []).length" class="cal-empty">—</div>
      </div>
    </div>
  </div>
</template>

<script>
// Calendar of the facility's appointments (GET appointments-calendar): physical, virtual and telemedicine,
// including telemedicine consultations the facility sent elsewhere.
const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const mondayOf = (d) => addDays(d, -((d.getDay() + 6) % 7));

export default {
  name: "AppointmentsCalendar",
  data() {
    return {
      mode: "month",
      cursor: new Date(),
      items: [],
      loading: false,
      today: iso(new Date()),
      weekdayNames: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    };
  },
  computed: {
    range() {
      if (this.mode === "week") {
        const start = mondayOf(this.cursor);
        return [start, addDays(start, 6)];
      }
      const first = new Date(this.cursor.getFullYear(), this.cursor.getMonth(), 1);
      const start = mondayOf(first);
      return [start, addDays(start, 41)];
    },
    monthDays() {
      const [start] = this.range;
      return Array.from({ length: 42 }, (_, i) => {
        const d = addDays(start, i);
        return { date: iso(d), num: d.getDate(), inMonth: d.getMonth() === this.cursor.getMonth() };
      });
    },
    weekDays() {
      const [start] = this.range;
      return Array.from({ length: 7 }, (_, i) => {
        const d = addDays(start, i);
        return { date: iso(d), label: d.toLocaleDateString("en-NG", { weekday: "short", day: "numeric", month: "short" }) };
      });
    },
    heading() {
      if (this.mode === "week") {
        const [s, e] = this.range;
        return `${s.toLocaleDateString("en-NG", { day: "numeric", month: "short" })} – ${e.toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" })}`;
      }
      return this.cursor.toLocaleDateString("en-NG", { month: "long", year: "numeric" });
    },
    byDate() {
      const map = {};
      this.items.forEach((a) => { (map[a.date] = map[a.date] || []).push(a); });
      return map;
    },
  },
  watch: {
    range() { this.load(); },
  },
  created() {
    this.load();
  },
  methods: {
    load() {
      const [from, to] = this.range;
      this.loading = true;
      this.axios
        .get("/api/v1/auth/appointments-calendar", { params: { from: iso(from), to: iso(to) } })
        .then((res) => { this.items = res.data.data || []; })
        .catch(() => { this.items = []; })
        .finally(() => { this.loading = false; });
    },
    move(step) {
      const c = new Date(this.cursor);
      if (this.mode === "week") c.setDate(c.getDate() + 7 * step);
      else c.setMonth(c.getMonth() + step, 1);
      this.cursor = c;
    },
    goToday() {
      this.cursor = new Date();
    },
    openWeek(date) {
      this.cursor = new Date(`${date}T12:00:00`);
      this.mode = "week";
    },
    tooltip(a) {
      return [`${a.time} (${a.minutes} min)`, a.patient, a.type, a.doctor, a.status, a.reason].filter(Boolean).join(" · ");
    },
  },
};
</script>

<style scoped>
.cal { background: #fff; border-radius: 12px; }
.cal-toolbar { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px; padding: 14px 16px; }
.cal-legend { font-size: .78rem; color: #475569; display: inline-flex; align-items: center; gap: 4px; }
.cal-dot { display: inline-block; width: 10px; height: 10px; border-radius: 50%; }
.cal-dot--physical { background: #64748b; }
.cal-dot--virtual { background: #2563eb; }
.cal-dot--telemedicine { background: #7c3aed; }
.cal-month { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); border-top: 1px solid #e2e8f0; }
.cal-wd { font-size: .72rem; text-transform: uppercase; color: #64748b; font-weight: 700; padding: 6px 8px; border-bottom: 1px solid #e2e8f0; }
.cal-cell { min-height: 104px; border-right: 1px solid #f1f5f9; border-bottom: 1px solid #f1f5f9; padding: 4px 6px; overflow: hidden; }
.cal-cell--out { background: #fafafa; color: #94a3b8; }
.cal-cell--today { background: #f5f3ff; }
.cal-num { font-size: .78rem; font-weight: 700; margin-bottom: 2px; }
.cal-ev { display: block; font-size: .72rem; border-radius: 5px; padding: 1px 5px; margin-bottom: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; color: #fff; }
.cal-ev:hover { color: #fff; opacity: .9; text-decoration: none; }
.cal-ev--physical { background: #64748b; }
.cal-ev--virtual { background: #2563eb; }
.cal-ev--telemedicine { background: #7c3aed; }
.cal-ev--st-declined, .cal-ev--st-cancelled { opacity: .45; text-decoration: line-through; }
.cal-more { border: none; background: none; font-size: .72rem; color: #4c1d95; padding: 0; }
.cal-week { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); border-top: 1px solid #e2e8f0; }
.cal-wcol { border-right: 1px solid #f1f5f9; padding: 6px; min-height: 260px; }
.cal-whead { font-size: .78rem; font-weight: 700; color: #374151; margin-bottom: 6px; }
.cal-wev { display: block; color: #fff; border-radius: 8px; padding: 6px 8px; margin-bottom: 6px; font-size: .76rem; line-height: 1.3; }
.cal-wev:hover { color: #fff; opacity: .9; text-decoration: none; }
.cal-wsub { opacity: .85; font-size: .7rem; }
.cal-empty { color: #cbd5e1; font-size: .8rem; }
@media (max-width: 768px) {
  .cal-month, .cal-week { grid-template-columns: repeat(7, minmax(44px, 1fr)); overflow-x: auto; }
  .cal-cell { min-height: 72px; }
  .cal-ev { font-size: .64rem; }
}
</style>
